import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card"
import { PaletteIcon, ScissorsIcon, BriefcaseIcon, HeartIcon, CameraIcon, CodeIcon } from "lucide-react"
import { useTranslation } from "react-i18next";

const templates = [
    {
        icon: PaletteIcon,
        titleKey: "templates.creative.title",
        descriptionKey: "templates.creative.description",
        gradient: "from-emerald-500 via-emerald-400/80 to-purple-500",
        accentColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
        icon: ScissorsIcon,
        titleKey: "templates.beautyWellness.title",
        descriptionKey: "templates.beautyWellness.description",
        gradient: "from-purple-500 via-purple-400/80 to-pink-500",
        accentColor: "text-purple-600 dark:text-purple-400",
    },
    {
        icon: BriefcaseIcon,
        titleKey: "templates.professional.title",
        descriptionKey: "templates.professional.description",
        gradient: "from-pink-500 via-pink-400/80 to-purple-500",
        accentColor: "text-pink-600 dark:text-pink-400",
    },
    {
        icon: HeartIcon,
        titleKey: "templates.healthFitness.title",
        descriptionKey: "templates.healthFitness.description",
        gradient: "from-emerald-500 via-green-500 to-teal",
        accentColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
        icon: CameraIcon,
        titleKey: "templates.photography.title",
        descriptionKey: "templates.photography.description",
        gradient: "from-amber-500 via-orange-500 to-rose-500",
        accentColor: "text-amber-600 dark:text-amber-400",
    },
    {
        icon: CodeIcon,
        titleKey: "templates.techDevelopment.title",
        descriptionKey: "templates.techDevelopment.description",
        gradient: "from-blue-500 via-indigo-500 to-violet",
        accentColor: "text-blue-600 dark:text-blue-400",
    },
]

export function TemplateShowcase() {
    const { t } = useTranslation('landing')

    return (
        <section id="templates" className="py-20 bg-gradient-to-bl from-emerald-500/25 to-pink-500/15">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-purple-600 to-pink-600 dark:from-emerald-400 dark:via-purple-400 dark:to-pink-400">
                        {t('templates.title')}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        {t('templates.subtitle')}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => (
                        <Card
                            key={template.titleKey}
                            className="overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        >
                            <CardHeader className="pb-2">
                                <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-4">
                                    {/* Mock template preview with gradient background */}
                                    <div className={`absolute inset-0 bg-gradient-to-br ${template.gradient} opacity-90`} />

                                    {/* Mock template UI elements */}
                                    <div className="absolute inset-0 p-4 flex flex-col">
                                        {/* Mock header */}
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm" />
                                            <div className="h-2 w-20 rounded bg-white/40 backdrop-blur-sm" />
                                        </div>
                                        {/* Mock content */}
                                        <div className="flex-1 grid grid-cols-2 gap-2">
                                            <div className="rounded-md bg-white/20 backdrop-blur-sm" />
                                            <div className="rounded-md bg-white/20 backdrop-blur-sm" />
                                            <div className="rounded-md bg-white/20 backdrop-blur-sm" />
                                            <div className="rounded-md bg-white/20 backdrop-blur-sm" />
                                        </div>
                                    </div>

                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <span className="text-white font-medium">{t('templates.previewTemplate')}</span>
                                    </div>
                                </div>

                                <CardTitle className="text-xl">{t(template.titleKey)}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground text-sm">{t(template.descriptionKey)}</p>
                                <span className={`inline-block mt-3 text-xs font-medium ${template.accentColor}`}>
                                    {t('templates.previewAvailable')}
                                </span>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    )
}
